/**
 * BookCatalogScreen: the Book screen's tab bodies, shared by the Mini App's `/book` and the studio
 * website's phone `/services` and `/events` (shared-client-screens D3, rows B1 to B6). Props only:
 * each side reads its own lists and hands in what a card opens; the header (CatalogHeader) and the
 * frame around it are each side's.
 *
 * Every service card opens its page, including while sessions are paused. Events load 15 at a
 * time with Load more; an event the client already holds a spot on opens their booking (B4).
 * Both tabs always show; an empty one says what will show up there (Gabriel 2026-10-21).
 */
import type { HubEventDto, HubServiceDto } from '@/api/types';
import { isViewableBooking } from '@/utils/bookingStatus';
import type { CatalogTab } from './CatalogHeader';
import { BookingsPausedNotice, CatalogError, CatalogSkeleton, NoEvents, NoServices } from './CatalogStates';
import { EventCatalogCard } from './EventCatalogCard';
import { ServiceCatalogCard } from './ServiceCatalogCard';
import styles from './BookSessionPage.module.css';

export interface CatalogRead<T> {
  status: 'loading' | 'error' | 'ready';
  items: T[];
  onRetry: () => void;
}

export interface BookCatalogScreenProps {
  tab: CatalogTab;
  /** The studio has paused new session bookings: the Services tab says so above its list. */
  paused: boolean;
  services: CatalogRead<HubServiceDto> & { onOpen: (service: HubServiceDto) => void };
  events: CatalogRead<HubEventDto> & {
    onOpen: (event: HubEventDto) => void;
    /** An event the client already holds a spot on: their booking. */
    onOpenBooking: (bookingId: string) => void;
    more: { hasNext: boolean; loading: boolean; load: () => void };
  };
}

function ServicesTab({ services }: Pick<BookCatalogScreenProps, 'services'>) {
  if (services.status === 'loading') return <CatalogSkeleton />;
  if (services.status === 'error') return <CatalogError onRetry={services.onRetry} />;
  if (services.items.length === 0) return <NoServices />;
  return (
    <div className={styles.servicesList}>
      {services.items.map((service) => (
        <ServiceCatalogCard key={service.id} service={service} onView={services.onOpen} />
      ))}
    </div>
  );
}

function EventsTab({ events }: Pick<BookCatalogScreenProps, 'events'>) {
  if (events.status === 'loading') return <CatalogSkeleton />;
  if (events.status === 'error') return <CatalogError onRetry={events.onRetry} />;
  if (events.items.length === 0) return <NoEvents />;
  return (
    <>
      <div className={styles.servicesList}>
        {events.items.map((event) => (
          <EventCatalogCard
            key={event.id}
            event={event}
            onView={(e) => (isViewableBooking(e.userBooking) ? events.onOpenBooking(e.userBooking.id) : events.onOpen(e))}
          />
        ))}
      </div>
      {events.more.hasNext && (
        <button
          type="button"
          className={styles.loadMore}
          onClick={events.more.load}
          disabled={events.more.loading}
          data-testid="catalog-load-more"
        >
          {events.more.loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </>
  );
}

export function BookCatalogScreen({ tab, paused, services, events }: BookCatalogScreenProps) {
  return (
    <div data-testid="book-session-page">
      {tab === 'services' ? (
        <div className={styles.servicesContent}>
          {paused && <BookingsPausedNotice />}
          <ServicesTab services={services} />
        </div>
      ) : (
        <EventsTab events={events} />
      )}
    </div>
  );
}
